import { useEffect, useRef, useState } from "react";
import { Upload, Loader2, Copy, Trash2, Film, FileText, Image as ImageIcon, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, formatDate, apiError } from "@/lib/api";

const kindOf = (ct) => (ct?.startsWith("video/") ? "video" : ct === "application/pdf" ? "pdf" : "image");
const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export default function MediaLibrary() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  const load = () => api.get("/admin/files").then((r) => setFiles(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/upload", fd);
      toast.success("File caricato");
      load();
    } catch (err) {
      toast.error(apiError(err, "Caricamento fallito"));
    } finally {
      setLoading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const copy = async (url) => {
    await navigator.clipboard.writeText(url);
    toast.success("URL copiato: incollalo dove serve");
  };

  const del = async (id) => {
    await api.delete(`/admin/files/${id}`).catch(() => {});
    toast.success("File eliminato");
    load();
  };

  return (
    <section data-testid="media-library">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-serif text-3xl text-forest">Libreria media</h2>
          <p className="mt-2 text-sm text-sage">Foto, video e documenti del sito. Copia l'URL e incollalo in articoli, prodotti e lavori.</p>
        </div>
        <button onClick={() => inputRef.current?.click()} disabled={loading} data-testid="media-upload-button" className="flex items-center gap-2 rounded-full bg-forest px-5 py-2.5 font-accent text-xs uppercase tracking-[0.18em] text-bone transition-transform duration-300 hover:scale-105 active:scale-95 disabled:opacity-60">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" strokeWidth={1.5} />}
          {loading ? "Caricamento…" : "Carica file"}
        </button>
        <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,application/pdf" className="hidden" onChange={upload} data-testid="media-upload-input" />
      </div>

      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {files.map((f) => {
          const kind = kindOf(f.content_type);
          return (
            <div key={f.id} className="rounded-sm border border-line bg-bone p-3" data-testid={`media-item-${f.id}`}>
              <div className="flex aspect-[4/3] items-center justify-center overflow-hidden rounded-sm bg-sand">
                {kind === "image" ? (
                  <img src={f.url} alt={f.original_filename} className="h-full w-full object-cover" loading="lazy" />
                ) : kind === "video" ? (
                  <Film className="h-8 w-8 text-sage" strokeWidth={1.5} />
                ) : (
                  <FileText className="h-8 w-8 text-sage" strokeWidth={1.5} />
                )}
              </div>
              <p className="mt-3 truncate text-sm font-medium text-forest" title={f.original_filename}>{f.original_filename}</p>
              <p className="mt-0.5 font-accent text-[10px] uppercase tracking-[0.18em] text-sage">{fmtSize(f.size)} · {formatDate(f.created_at)}</p>
              <div className="mt-3 flex gap-2">
                <button onClick={() => copy(f.url)} className="flex flex-1 items-center justify-center gap-1.5 rounded-full border border-line py-2 font-accent text-[10px] uppercase tracking-[0.15em] text-forest hover:bg-sand" data-testid={`media-copy-${f.id}`}>
                  <Copy className="h-3 w-3" /> URL
                </button>
                <a href={f.url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center rounded-full border border-line px-3 py-2 text-forest hover:bg-sand" data-testid={`media-open-${f.id}`} aria-label="Apri">
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
                <button onClick={() => del(f.id)} className="flex items-center justify-center rounded-full border border-line px-3 py-2 text-destructive hover:bg-sand" data-testid={`media-delete-${f.id}`} aria-label="Elimina">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
      {files.length === 0 && (
        <p className="mt-8 rounded-sm border border-line bg-bone px-5 py-10 text-center text-sage" data-testid="media-empty">
          <ImageIcon className="mx-auto mb-3 h-6 w-6 text-line" strokeWidth={1.5} />
          Nessun file ancora. Carica il primo.
        </p>
      )}
    </section>
  );
}
